import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useBlocker, useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { AppBreadcrumb, AppContainer, AppTypography } from '@/components/atoms';
import { LoadingTemplate, SiteAdminNotFoundTemplate } from '@/components/templates';
import OrderRezeptSheetEditForm from '@/components/organisms/OrderRezeptSheetEditForm';
import {
  useAdminOrderRezeptSheetDetail,
  useUpdateOrderRezeptSheetMutation,
} from '@/hooks/admin/useAdminOrderRezeptSheets';
import { getServiceTypeLabel } from '@/constants/serviceType';
import { convGtJDate } from '@/utils/dateConverter';
import { isNotFoundError } from '@/utils/apiError';
import { FORM_MESSAGES } from '@/constants/formMessages';
import type { UpdateOrderRezeptSheetPayload } from '@/services/admin/order-rezept-sheets';
import { getSiteTypeLabel } from '@/shared/constants/siteTypes';

const OrderRezeptSheetEditPage: React.FC = () => {
  const { site, id } = useParams<{ site: string; id: string }>();
  const currentSite = site ?? 'medix';
  const recordId = Number(id) || 0;
  const navigate = useNavigate();
  const location = useLocation();

  const locationState = location.state as { listSearch?: string } | null;
  const listSearch = locationState?.listSearch ?? '';
  const listPath = `/${currentSite}/admin/order_rezept_sheets${listSearch}`;

  const dirtyRef = useRef(false);

  // Browser tab close / refresh → beforeunload
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // React Router navigation (back button, sidebar links) → window.confirm
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return dirtyRef.current && currentLocation.pathname !== nextLocation.pathname;
  });

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    const shouldLeave = window.confirm(FORM_MESSAGES.DISCARD_CHANGES_CONFIRMATION);
    if (shouldLeave) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker.state]);

  const { data, isLoading, isError, error } = useAdminOrderRezeptSheetDetail(
    site ?? null,
    recordId
  );
  const { updateOrderMutation, isLoading: isSubmitting } = useUpdateOrderRezeptSheetMutation(
    site ?? null
  );

  const order = data?.data;

  const confirmAndNavigate = useCallback(
    (path: string) => {
      if (dirtyRef.current) {
        if (window.confirm(FORM_MESSAGES.DISCARD_CHANGES_CONFIRMATION)) {
          dirtyRef.current = false; // Bypass blocker after confirm
          navigate(path);
        }
      } else {
        navigate(path);
      }
    },
    [navigate]
  );

  const breadcrumbItems = useMemo(
    () => [
      {
        title: 'Home',
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          confirmAndNavigate(`/${currentSite}/admin`);
        },
        href: `/${currentSite}/admin`,
      },
      {
        title: 'レセプト用紙受注状況',
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          confirmAndNavigate(listPath);
        },
        href: listPath,
      },
      { title: `編集:${recordId}` },
    ],
    [currentSite, recordId, listPath, confirmAndNavigate]
  );

  const handleSubmit = useCallback(
    (payload: UpdateOrderRezeptSheetPayload) => {
      updateOrderMutation({
        id: recordId,
        payload,
      })
        .then(response => {
          if (response?.success) {
            dirtyRef.current = false; // Bypass blocker after successful save
            setTimeout(() => navigate(listPath, { replace: true }), 300);
          }
        })
        .catch(() => {
          // Error toast is shown by the mutation hook's onError; stay on page.
        });
    },
    [updateOrderMutation, recordId, navigate, listPath]
  );

  const handleCancel = useCallback(() => {
    confirmAndNavigate(listPath);
  }, [confirmAndNavigate, listPath]);

  const handleDirtyChange = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);

  // 404 handling
  if (isError && isNotFoundError(error)) {
    return <SiteAdminNotFoundTemplate />;
  }

  if (isLoading) return <LoadingTemplate />;

  if (isError || !order) return <SiteAdminNotFoundTemplate />;

  const siteTypeDisplay = order.site_type
    ? getSiteTypeLabel(order.site_type)
    : '';
  const serviceTypeLabel = getServiceTypeLabel(order.service_type);
  const orderDateWareki = order.order_date ? convGtJDate(dayjs(order.order_date)) : '';

  return (
    <AppContainer className="py-4">
      <AppBreadcrumb items={breadcrumbItems} />
      <AppTypography.H4 className="my-4 !font-normal">
        レセプト用紙受注状況編集:{recordId}
      </AppTypography.H4>

      <div className="mb-6 rounded border bg-white p-4">
        <div className="grid grid-cols-2 gap-x-8 gap-y-3 md:grid-cols-3">
          <div>
            <span className="font-medium text-gray-600">治療院</span>
            <p>{order.clinic_name ?? order.clinic_number ?? ''}</p>
          </div>
          <div>
            <span className="font-medium text-gray-600">サイト区分</span>
            <p>{siteTypeDisplay}</p>
          </div>
          <div>
            <span className="font-medium text-gray-600">注文番号</span>
            <p>{order.order_no}</p>
          </div>
          <div>
            <span className="font-medium text-gray-600">注文日</span>
            <p>{orderDateWareki}</p>
          </div>
          <div>
            <span className="font-medium text-gray-600">サービス区分</span>
            <p>{serviceTypeLabel}</p>
          </div>
        </div>
      </div>

      <div className="rounded border bg-white p-4">
        <OrderRezeptSheetEditForm
          initialDetails={order.details ?? []}
          rezeptSheets={order.rezept_sheets ?? []}
          subtotal={order.subtotal}
          deliveryFeeTotal={order.delivery_fee}
          total={order.total}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          onCancel={handleCancel}
          onDirtyChange={handleDirtyChange}
        />
      </div>
    </AppContainer>
  );
};

export default OrderRezeptSheetEditPage;
